/**
 * Cookies.jsx — EVORA Cookies Policy Page
 *
 * ✅ Refactored: mobile-first, semantic HTML5, JSON-LD, perf-optimised,
 *    conversion-friendly layout, WCAG AA accessible, print-ready.
 */

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import { IconCookie, IconArrowRight } from '../../components/common/Icons/Icons';
import './Cookies.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const SITE_URL      = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME    = BRAND?.name || 'EVORA';
const COOKIES_URL   = `${SITE_URL}/cookies`;
const OG_IMAGE      = `${SITE_URL}/og-image.jpg`;
const CONTACT_EMAIL = BRAND?.email || 'privacy@evoraclothing.com';
const ISO_DATE   = '2026-01-01';
const LAST_UPDATED = 'January 1, 2026';

// ── JSON-LD: hoisted to module scope (zero re-serialization on render) ─────────
const SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${COOKIES_URL}#webpage`,
      url: COOKIES_URL,
      name: `Cookies Policy | ${BRAND_NAME}`,
      description: `Learn how ${BRAND_NAME} uses cookies and similar technologies to enhance your browsing experience.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${COOKIES_URL}#breadcrumb` },
      dateModified: ISO_DATE,
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${COOKIES_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Cookies Policy', item: COOKIES_URL },
      ],
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: BRAND_NAME,
      url: SITE_URL,
      contactPoint: {
        '@type': 'ContactPoint',
        email: CONTACT_EMAIL,
        contactType: 'customer support',
        areaServed: 'IN',
      },
    },
  ],
});

// ── Section data (keeps JSX lean; easy to update copy without touching markup) ─
const SECTIONS = [
  {
    id: 'what-are-cookies',
    heading: '1. What Are Cookies?',
    content: (
      <>
        <p>
          Cookies are small text files stored on your device when you visit a website.
          They allow the site to recognise your device and remember preferences or
          actions across pages and visits — giving you a faster, more personalised
          experience.
        </p>
        <p>
          Cookies do not contain personally identifiable information by themselves;
          they serve as lightweight session identifiers that help us deliver the
          experience you expect.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    heading: '2. How We Use Cookies',
    content: (
      <>
        <p>We use cookies for four distinct purposes:</p>
        <ul className="cookies-list">
          <li><strong>Essential:</strong> Core functionality — security, authentication, cart persistence. Cannot be disabled without breaking the site.</li>
          <li><strong>Performance:</strong> Anonymous analytics that help us understand traffic patterns and improve load times.</li>
          <li><strong>Functionality:</strong> Stored preferences such as language, currency, and wishlist state for a seamless return visit.</li>
          <li><strong>Advertising:</strong> Interest-based ads and campaign attribution via our third-party advertising partners.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'types',
    heading: '3. Types of Cookies We Use',
    content: (
      <>
        <h3 className="cookies-section__subtitle">3.1 Strictly Necessary</h3>
        <p>
          Always active. Set in direct response to your actions — logging in, adding
          items to cart, setting privacy preferences. No opt-out mechanism applies
          without impairing core features.
        </p>
        <p><strong>Examples:</strong> Session tokens, CSRF protection, secure checkout state</p>

        <h3 className="cookies-section__subtitle">3.2 Performance &amp; Analytics</h3>
        <p>
          Aggregate, anonymised data on how visitors navigate the site. No personally
          identifiable information is stored or shared.
        </p>
        <p><strong>Examples:</strong> Google Analytics (_ga, _gid), Core Web Vitals telemetry</p>

        <h3 className="cookies-section__subtitle">3.3 Functionality</h3>
        <p>
          Optional enhancements. If disabled, the site still works but you may need
          to re-enter preferences on every visit.
        </p>
        <p><strong>Examples:</strong> Language, region, recently-viewed products</p>

        <h3 className="cookies-section__subtitle">3.4 Targeting &amp; Advertising</h3>
        <p>
          Set by our advertising partners to build interest profiles and measure ad
          effectiveness. These partners operate under their own privacy policies.
        </p>
        <p><strong>Examples:</strong> Meta Pixel, Google Ads remarketing, retargeting pixels</p>
      </>
    ),
  },
  {
    id: 'third-party',
    heading: '4. Third-Party Cookies',
    content: (
      <>
        <p>
          Certain third-party services embedded in our site may place their own
          cookies on your device:
        </p>
        <ul className="cookies-list">
          <li><strong>Analytics Providers:</strong> Google Analytics — anonymised usage statistics</li>
          <li><strong>Payment Processors:</strong> Razorpay, Stripe — secure transaction state</li>
          <li><strong>Social Platforms:</strong> Meta, Twitter/X — social login and sharing features</li>
          <li><strong>Advertising Networks:</strong> Google Ads, Meta Ads — personalised ad delivery</li>
        </ul>
        <p>
          We do not control third-party cookies. Please review the privacy policy of
          each provider listed above for full details on their data practices.
        </p>
      </>
    ),
  },
  {
    id: 'managing',
    heading: '5. Managing Your Preferences',
    content: (
      <>
        <p>
          You have full control over non-essential cookies. You can accept, reject,
          or customise your preferences at any time.
        </p>

        <h3 className="cookies-section__subtitle">5.1 Browser Settings</h3>
        <ul className="cookies-list">
          <li><strong>Chrome:</strong> Settings → Privacy &amp; security → Cookies and other site data</li>
          <li><strong>Firefox:</strong> Options → Privacy &amp; Security → Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
          <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and site permissions</li>
        </ul>

        <h3 className="cookies-section__subtitle">5.2 Our Consent Banner</h3>
        <p>
          On your first visit, our consent banner lets you accept all, reject
          non-essential, or customise individual categories. You can revisit your
          choices at any time via the Cookie Settings link in our footer.
        </p>

        <h3 className="cookies-section__subtitle">5.3 Impact of Disabling Cookies</h3>
        <p>
          Disabling essential cookies will impair cart functionality, account login,
          and secure checkout. Disabling other categories has no functional impact
          but reduces personalisation.
        </p>
      </>
    ),
  },
  {
    id: 'storage',
    heading: '6. Local Storage &amp; Similar Technologies',
    content: (
      <>
        <p>
          Alongside cookies, we use <strong>localStorage</strong> and{' '}
          <strong>sessionStorage</strong> for client-side state (e.g., draft cart
          before login) and <strong>web beacons</strong> (1×1 pixel tags) to confirm
          email opens and track campaign conversion. These technologies are governed
          by the same consent preferences as cookies.
        </p>
      </>
    ),
  },
  {
    id: 'duration',
    heading: '7. Cookie Duration',
    content: (
      <>
        <h3 className="cookies-section__subtitle">7.1 Session Cookies</h3>
        <p>
          Exist only during your active browser session. Automatically deleted when
          you close your browser tab or window.
        </p>

        <h3 className="cookies-section__subtitle">7.2 Persistent Cookies</h3>
        <p>
          Stored for a defined period — typically 30 days to 2 years — or until you
          manually delete them. Used to recognise returning visitors and restore
          preferences without requiring re-entry.
        </p>
      </>
    ),
  },
  {
    id: 'updates',
    heading: '8. Policy Updates',
    content: (
      <>
        <p>
          We may revise this policy to reflect changes in technology, regulation, or
          our own practices. When we do, we update the "Last updated" date above and,
          for material changes, notify you via email or an in-app banner.
        </p>
        <p>
          We recommend reviewing this page periodically to stay informed.
        </p>
      </>
    ),
  },
];

// ── Component ──────────────────────────────────────────────────────────────────
export default function Cookies() {
  return (
    <>
      {/* ── SEO Head ──────────────────────────────────────────────────────── */}
      <Helmet>
        {/* Primary meta */}
        <title>Cookies Policy | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`Learn how ${BRAND_NAME} uses cookies and similar technologies to enhance your shopping experience. Manage your preferences anytime.`}
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={COOKIES_URL} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={COOKIES_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={`Cookies Policy | ${BRAND_NAME}`} />
        <meta property="og:description" content={`How ${BRAND_NAME} uses cookies to deliver a seamless, personalised shopping experience.`} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Cookies Policy — ${BRAND_NAME}`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Cookies Policy | ${BRAND_NAME}`} />
        <meta name="twitter:description" content={`How ${BRAND_NAME} uses cookies. Manage your preferences anytime.`} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={`Cookies Policy — ${BRAND_NAME}`} />

        <script type="application/ld+json">{SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        {/*
          Skip-to-content anchor — critical for keyboard & screen-reader users.
          Pair with a visually-hidden skip link in Layout that targets #main-content.
        */}
        <main className="cookies-page" id="main-content">

          {/* ── Breadcrumb ────────────────────────────────────────────────── */}
          <nav className="cookies-breadcrumb" aria-label="Breadcrumb">
            <ol className="cookies-breadcrumb__list">
              <li>
                <Link to="/" className="cookies-breadcrumb__link">Home</Link>
              </li>
              {/* aria-hidden separator keeps screen readers clean */}
              <li className="cookies-breadcrumb__sep" aria-hidden="true" />
              <li>
                <span aria-current="page" className="cookies-breadcrumb__current">
                  Cookies Policy
                </span>
              </li>
            </ol>
          </nav>

          {/* ── Hero ──────────────────────────────────────────────────────── */}
          {/*
            WHY <header>: Wrapping the hero in <header> scopes it to the <main>
            landmark, improving screen-reader document outline without conflicting
            with the page-level <header> inside Layout.
          */}
          <header className="cookies-hero">
            <div className="cookies-hero__icon" aria-hidden="true">
              <IconCookie size={48} />
            </div>
            {/* Single H1 per page — critical for SEO */}
            <h1 className="cookies-hero__title">Cookies Policy</h1>
            <p className="cookies-hero__subtitle">
              We use cookies to deliver a seamless, personalised experience.
              This page explains exactly what we collect, why, and how to manage it.
            </p>
            <p className="cookies-hero__updated">
              Last updated: <time dateTime={ISO_DATE}>{LAST_UPDATED}</time>
            </p>
          </header>

          {/* ── Policy Sections ───────────────────────────────────────────── */}
          {/*
            WHY <article>: The policy is a self-contained, reusable content unit —
            semantically correct and improves crawlability.
          */}
          <article className="cookies-content" aria-label="Cookies policy content">

            {/* Render standard sections from data array (DRY, easy copy updates) */}
            {SECTIONS.map(({ id, heading, content }) => (
              <section
                key={id}
                className="cookies-section"
                aria-labelledby={`${id}-heading`}
              >
                <h2 id={`${id}-heading`} className="cookies-section__title">
                  {heading}
                </h2>
                <div className="cookies-section__content">
                  {content}
                </div>
              </section>
            ))}

            {/* ── Contact (highlighted section) ─────────────────────────── */}
            <section
              className="cookies-section cookies-section--highlight"
              aria-labelledby="contact-heading"
            >
              <h2 id="contact-heading" className="cookies-section__title">
                9. Contact Us
              </h2>
              <div className="cookies-section__content">
                <p>
                  Questions about this policy or our cookie practices? Reach us via:
                </p>
                <address className="cookies-contact">
                  {/*
                    WHY <address>: Semantically marks contact info, improving
                    accessibility and structured-data inference.
                  */}
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="cookies-link">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                  <p>
                    <strong>Contact Form:</strong>{' '}
                    <Link to="/contact" className="cookies-link">
                      Visit our Contact page
                    </Link>
                  </p>
                  <p>
                    <strong>Registered Address:</strong> {BRAND_NAME}, India
                  </p>
                </address>
                <p>
                  For full details on personal data handling, see our{' '}
                  <Link to="/privacy" className="cookies-link">Privacy Policy</Link>.
                </p>
              </div>
            </section>

          </article>

          {/* ── Related (internal linking) ─────────────────────────────────── */}
          <nav className="cookies-related" aria-label="Related information">
            <h2 className="cookies-related__title">Related information</h2>
            <ul className="cookies-related__list" role="list">
              <li><Link to="/privacy" className="cookies-related__link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="cookies-related__link">Terms of Service</Link></li>
              <li><Link to="/contact" className="cookies-related__link">Contact Us</Link></li>
              <li><Link to="/faq" className="cookies-related__link">FAQ</Link></li>
            </ul>
          </nav>

          {/* ── CTA ───────────────────────────────────────────────────────── */}
          <section className="cookies-cta" aria-labelledby="cta-heading">
            <h2 id="cta-heading" className="cookies-cta__title">
              Questions About Cookies?
            </h2>
            <p className="cookies-cta__text">
              Our team is happy to walk you through your options or update your
              preferences on your behalf.
            </p>
            <div className="cookies-cta__actions">
              <Link
                to="/contact"
                className="cookies-cta__btn cookies-cta__btn--primary"
              >
                Contact Us
                <IconArrowRight size={14} aria-hidden="true" />
              </Link>
              <Link
                to="/privacy"
                className="cookies-cta__btn cookies-cta__btn--ghost"
              >
                View Privacy Policy
                <IconArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
          </section>

        </main>
      </Layout>
    </>
  );
}